// BubbleMap.jsx
// SVG-based interactive map of the 8 pillars arranged in a circle around
// the user's company name. Reflects completion status from central memory.
// The first incomplete pillar pulses to indicate the recommended next step.
// Clicking any pillar bubble navigates to /pillar/:pillarId.

import { useNavigate } from 'react-router-dom';

const PILLARS = [
  {
    id: 'product-definition',
    icon: '🎯',
    lines: ['Product', 'Definition'],
  },
  {
    id: 'market-research',
    icon: '🔍',
    lines: ['Market', 'Research'],
  },
  {
    id: 'finance-pricing',
    icon: '💰',
    lines: ['Finance &', 'Pricing'],
  },
  {
    id: 'regulatory-tax',
    icon: '⚖️',
    lines: ['Regulatory', '& Tax'],
  },
  {
    id: 'go-to-market',
    icon: '🚀',
    lines: ['Go-To-', 'Market'],
  },
  {
    id: 'operations-admin',
    icon: '⚙️',
    lines: ['Operations', '& Admin'],
  },
  {
    id: 'sales-partnerships',
    icon: '🤝',
    lines: ['Sales &', 'Partners'],
  },
  {
    id: 'brand-marketing',
    icon: '📢',
    lines: ['Brand &', 'Marketing'],
  },
];

// SVG layout constants
const CX = 400;          // center x
const CY = 340;          // center y
const ORBIT = 215;       // orbit radius
const CENTER_R = 78;     // central bubble radius
const PILLAR_R = 56;     // pillar bubble radius

// Compute (x, y) for each pillar, starting from top and going clockwise
function pillarPosition(index) {
  const angleDeg = (index / 8) * 360 - 90;
  const angleRad = (angleDeg * Math.PI) / 180;
  return {
    x: CX + ORBIT * Math.cos(angleRad),
    y: CY + ORBIT * Math.sin(angleRad),
  };
}

export default function BubbleMap({ companyName, completedPillars = [] }) {
  const navigate = useNavigate();

  // The recommended pillar is the first one in order that isn't completed yet.
  const recommendedId = PILLARS.find(
    (pillar) => !completedPillars.includes(pillar.id)
  )?.id;

  return (
    <div style={styles.wrapper}>
      {/* keyframes injected once — CSS modules not available here */}
      <style>{`
        @keyframes pulse-ring {
          0%   { r: 60; opacity: 0.7; }
          50%  { r: 78; opacity: 0;   }
          100% { r: 60; opacity: 0.7; }
        }
        @keyframes soft-scale {
          0%, 100% { transform: scale(1);   }
          50%       { transform: scale(1.06); }
        }
        .pillar-bubble {
          cursor: pointer;
          transition: opacity 0.15s;
        }
        .pillar-bubble:hover {
          opacity: 0.85;
        }
      `}</style>

      <svg
        viewBox="0 0 800 680"
        width="100%"
        style={styles.svg}
        aria-label="Rocket-Fuel pillar map"
      >
        {/* ── Lines from center to each pillar ───────────────────────── */}
        {PILLARS.map((pillar, index) => {
          const { x, y } = pillarPosition(index);
          return (
            <line
              key={`line-${pillar.id}`}
              x1={CX}
              y1={CY}
              x2={x}
              y2={y}
              stroke="rgba(255,255,255,0.12)"
              strokeWidth={1.5}
            />
          );
        })}

        {/* ── Central company bubble ──────────────────────────────────── */}
        <circle
          cx={CX}
          cy={CY}
          r={CENTER_R}
          fill="#FF6B35"
          filter="url(#centerGlow)"
        />
        <text
          x={CX}
          y={CY - 10}
          textAnchor="middle"
          fill="#fff"
          fontSize={14}
          fontWeight="700"
          fontFamily="sans-serif"
          style={{ pointerEvents: 'none', userSelect: 'none' }}
        >
          {companyName.length > 14
            ? companyName.slice(0, 13) + '…'
            : companyName}
        </text>
        <text
          x={CX}
          y={CY + 10}
          textAnchor="middle"
          fill="rgba(255,255,255,0.7)"
          fontSize={11}
          fontFamily="sans-serif"
          style={{ pointerEvents: 'none', userSelect: 'none' }}
        >
          your company
        </text>

        {/* ── Pillar bubbles ──────────────────────────────────────────── */}
        {PILLARS.map((pillar, index) => {
          const { x, y } = pillarPosition(index);
          const isComplete = completedPillars.includes(pillar.id);
          const isRecommended = pillar.id === recommendedId;

          const bubbleFill = isComplete ? '#1A6B3C' : '#0F3460';
          const strokeColor = isComplete
            ? '#4CAF50'
            : isRecommended
            ? '#FF6B35'
            : '#1E4D8C';

          return (
            <g
              key={pillar.id}
              className="pillar-bubble"
              onClick={() => navigate(`/pillar/${pillar.id}`)}
              role="button"
              aria-label={pillar.lines.join(' ')}
            >
              {/* Pulsing ring for the recommended pillar */}
              {isRecommended && (
                <circle
                  cx={x}
                  cy={y}
                  r={PILLAR_R + 4}
                  fill="none"
                  stroke="#FF6B35"
                  strokeWidth={2}
                >
                  <animate
                    attributeName="r"
                    values={`${PILLAR_R + 4};${PILLAR_R + 20};${PILLAR_R + 4}`}
                    dur="2.4s"
                    repeatCount="indefinite"
                  />
                  <animate
                    attributeName="opacity"
                    values="0.7;0;0.7"
                    dur="2.4s"
                    repeatCount="indefinite"
                  />
                </circle>
              )}

              {/* Main bubble */}
              <circle
                cx={x}
                cy={y}
                r={PILLAR_R}
                fill={bubbleFill}
                stroke={strokeColor}
                strokeWidth={isRecommended ? 2 : 1.5}
              />

              {/* Icon */}
              <text
                x={x}
                y={y - 14}
                textAnchor="middle"
                fontSize={20}
                style={{ pointerEvents: 'none', userSelect: 'none' }}
              >
                {pillar.icon}
              </text>

              {/* Label line 1 */}
              <text
                x={x}
                y={y + 8}
                textAnchor="middle"
                fill="#fff"
                fontSize={10.5}
                fontWeight="600"
                fontFamily="sans-serif"
                style={{ pointerEvents: 'none', userSelect: 'none' }}
              >
                {pillar.lines[0]}
              </text>

              {/* Label line 2 */}
              <text
                x={x}
                y={y + 22}
                textAnchor="middle"
                fill="rgba(255,255,255,0.75)"
                fontSize={10.5}
                fontFamily="sans-serif"
                style={{ pointerEvents: 'none', userSelect: 'none' }}
              >
                {pillar.lines[1]}
              </text>

              {/* Completed checkmark badge */}
              {isComplete && (
                <>
                  <circle
                    cx={x + PILLAR_R - 10}
                    cy={y - PILLAR_R + 10}
                    r={10}
                    fill="#4CAF50"
                  />
                  <text
                    x={x + PILLAR_R - 10}
                    y={y - PILLAR_R + 14}
                    textAnchor="middle"
                    fill="#fff"
                    fontSize={12}
                    fontWeight="700"
                    fontFamily="sans-serif"
                    style={{ pointerEvents: 'none', userSelect: 'none' }}
                  >
                    ✓
                  </text>
                </>
              )}
            </g>
          );
        })}

        {/* ── Filters ────────────────────────────────────────────────── */}
        <defs>
          <filter id="centerGlow" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="6" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
      </svg>
    </div>
  );
}

const styles = {
  wrapper: {
    width: '100%',
    maxWidth: '760px',
    margin: '0 auto',
  },
  svg: {
    display: 'block',
    overflow: 'visible',
  },
};
